<template>
  <div v-if="error" class="import-error">
    <div v-for="block in errorRows" :key="block.label" class="import-error__block">
      <b>{{ block.label }}</b>
      <span>{{ block.text }}</span>
    </div>
    <button type="button" class="import-error__remove" @click="$emit('remove')">{{ $t('import_files.error_remove') }}</button>
  </div>
  <template v-else-if="entry.preview">
    <p v-if="sec.showPickHint" class="import-hint">{{ $t('import_files.pick_hint') }}</p>

    <div class="import-label">{{ $t('import_files.section_import_as') }}</div>
    <div class="import-options" role="radiogroup" :aria-label="$t('import_files.section_import_as')">
      <button
        v-for="choice in sec.options"
        :key="choice"
        type="button"
        role="radio"
        class="import-option"
        :aria-checked="entry.settings.choice === choice"
        :data-choice="choice"
        @click="$emit('choose', choice)"
      >
        <span class="import-icon" :class="`tone-${iconFor(choice).tone}`" aria-hidden="true"><i class="material-icons">{{ iconFor(choice).name }}</i></span>
        <span class="import-option__text">
          <span class="import-option__name">
            {{ optionName(t, choice) }}
            <span v-if="badgeText(choice)" class="import-option__badge">{{ badgeText(choice) }}</span>
          </span>
          <span class="import-option__desc" :class="{ cross: goesElsewhere(state, choice) }">{{ describe(choice) }}</span>
        </span>
        <span class="import-check" aria-hidden="true"></span>
      </button>
    </div>

    <div v-if="sec.showRouteRetry" class="import-route-failed" role="alert">
      <span>{{ $t('import_files.failed_no_route') }}</span>
      <button type="button" class="import-route-failed__retry" data-test="route-retry" @click="$emit('retryRoute')">{{ $t('import_files.route_try_again') }}</button>
    </div>

    <template v-if="sec.showDays">
      <div class="import-label">{{ $t('import_files.section_days') }}</div>
      <div class="import-options" role="radiogroup" :aria-label="$t('import_files.section_days')">
        <button type="button" role="radio" class="import-option" :aria-checked="!entry.settings.joinDays" @click="$emit('joinDays', false)">
          <span class="import-icon tone-white" aria-hidden="true"><i class="material-icons">route</i></span>
          <span class="import-option__text">
            <span class="import-option__name">{{ $t('import_files.days_n', sec.dayCount) }}</span>
            <span class="import-option__desc">{{ $t('import_files.days_per_stage') }}</span>
          </span>
          <span class="import-check" aria-hidden="true"></span>
        </button>
        <button type="button" role="radio" class="import-option" :aria-checked="entry.settings.joinDays" @click="$emit('joinDays', true)">
          <span class="import-icon tone-white" aria-hidden="true"><i class="material-icons">route</i></span>
          <span class="import-option__text">
            <span class="import-option__name">{{ $t('import_files.days_one') }}</span>
            <span class="import-option__desc">{{ $t('import_files.days_join') }}</span>
          </span>
          <span class="import-check" aria-hidden="true"></span>
        </button>
      </div>
    </template>

    <template v-if="sec.showLines">
      <div class="import-label">{{ $t('import_files.section_lines') }}</div>
      <button type="button" role="checkbox" class="import-option import-option--check" :aria-checked="entry.settings.includeCourse" @click="$emit('includeCourse', !entry.settings.includeCourse)">
        <span class="import-option__text">
          <span class="import-option__name">{{ $t('import_files.lines_include_course') }}</span>
          <span class="import-option__desc">{{ $t('import_files.lines_include_course_desc') }}</span>
        </span>
        <span class="import-check" aria-hidden="true"></span>
      </button>
    </template>

    <template v-if="sec.showWaypoints">
      <div class="import-label">{{ $t('import_files.dest_waypoints') }}</div>
      <button type="button" role="checkbox" class="import-option import-option--check" :aria-checked="entry.settings.keepWaypoints" @click="$emit('keepWaypoints', !entry.settings.keepWaypoints)">
        <span class="import-option__text">
          <span class="import-option__name">{{ $t('import_files.waypoints_named', sec.namedWaypoints) }}</span>
          <span class="import-option__desc">{{ sec.waypointsAsHighlights ? $t('import_files.waypoints_as_highlights') : $t('import_files.waypoints_as_group') }}</span>
        </span>
        <span class="import-check" aria-hidden="true"></span>
      </button>
      <button v-if="sec.repeatedPoints > 0" type="button" role="checkbox" class="import-option import-option--check" :aria-checked="entry.settings.includeRepeats" @click="$emit('includeRepeats', !entry.settings.includeRepeats)">
        <span class="import-option__text">
          <span class="import-option__name">{{ $t('import_files.waypoints_repeats', sec.repeatedPoints) }}</span>
          <span class="import-option__desc">{{ $t('import_files.waypoints_repeats_desc') }}</span>
        </span>
        <span class="import-check" aria-hidden="true"></span>
      </button>
    </template>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  badge,
  goesElsewhere,
  intentDestination,
  sections,
  type ImportChoice,
  type ImportFileEntry,
  type ImportScreenState
} from '../../utils/import/importScreenState';
import { keptWaypoints } from '../../utils/import/importPayloads';
import { errorBlocks, importIcon, optionDescription, optionName } from '../../utils/import/importTexts';

// An open file's settings — the same for every file, with only the ones that apply: Import as,
// Days, Lines and Waypoints. An unreadable file shows what's wrong and how to fix it instead.
const props = defineProps<{ state: ImportScreenState; entry: ImportFileEntry }>();
defineEmits<{
  (e: 'choose', choice: ImportChoice): void;
  (e: 'joinDays', join: boolean): void;
  (e: 'includeCourse', include: boolean): void;
  (e: 'keepWaypoints', keep: boolean): void;
  (e: 'includeRepeats', include: boolean): void;
  (e: 'remove'): void;
  (e: 'retryRoute'): void;
}>();

const { t } = useI18n();

const error = computed(() => props.entry.preview?.error ?? null);
const sec = computed(() => sections(props.state, props.entry));
const waypointCount = computed(() => (props.entry.preview ? keptWaypoints(props.entry.preview).length : 0));

const errorRows = computed(() => {
  if (!error.value) return [];
  const { what, why, fix } = errorBlocks(t, error.value);
  return [
    { label: t('import_files.error_whats_wrong'), text: what },
    { label: t('import_files.error_likely_cause'), text: why },
    { label: t('import_files.error_what_to_do'), text: fix }
  ];
});

const iconFor = (choice: ImportChoice) => importIcon(choice, false);

function badgeText(choice: ImportChoice): string | null {
  const b = badge(props.state, props.entry, choice);
  if (b === 'recommended') return t('import_files.badge_recommended');
  return null;
}

const describe = (choice: ImportChoice): string =>
  optionDescription(t, choice, waypointCount.value, intentDestination(props.state.intent), goesElsewhere(props.state, choice));
</script>

<style scoped>
.import-hint {
  margin: 0;
  font-size: 13px;
  color: var(--text-muted);
}
.import-label {
  margin: 6px 2px 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
}
.import-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.import-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  /* A button doesn't wrap by default here; long names and descriptions must. */
  white-space: normal;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: var(--gray-raised);
  color: var(--white);
  text-align: left;
  cursor: pointer;
}
.import-option:hover {
  background: var(--gray-hover);
}
.import-option__text {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.import-option__name {
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.import-option__badge {
  font-size: 10px;
  font-weight: 700;
  line-height: 1.5;
  padding: 0 5px;
  border-radius: 4px;
  background: var(--yellow);
  color: var(--dark);
}
.import-option__desc {
  font-size: 12px;
  color: var(--text-muted);
}
.import-option__desc.cross {
  color: var(--blue);
}
.import-check {
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid var(--gray-border);
  display: grid;
  place-items: center;
}
.import-option[aria-checked='true'] .import-check {
  background: var(--yellow);
  border-color: var(--yellow);
}
.import-option[aria-checked='true'] .import-check::after {
  content: '';
  width: 11px;
  height: 6px;
  border: solid var(--dark);
  border-width: 0 0 2.5px 2.5px;
  transform: rotate(-45deg) translate(1px, -1px);
}
.import-error {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.import-error__block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--gray-raised);
  font-size: 13px;
}
.import-error__block b {
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
}
.import-route-failed {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--gray-raised);
  color: var(--red);
  font-size: 13px;
}
.import-route-failed__retry {
  flex: none;
  padding: 6px 12px;
  border-radius: 8px;
  border: 0;
  background: var(--white);
  color: #111;
  font-weight: 700;
  cursor: pointer;
}
.import-error__remove {
  padding: 10px;
  border-radius: 10px;
  border: 0;
  background: var(--gray-raised);
  color: var(--white);
  font-weight: 700;
  cursor: pointer;
}
</style>
